import { Button, FormControl, FormControlLabel, FormHelperText, IconButton, Input, InputLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ChromePicker, ColorResult } from 'react-color';
import { FaTrash } from 'react-icons/fa';
import { createSlider, uploadFile, editSlider, getSliderById, getSliders, editSliderOrder, getImage, getZones } from '../../server/server';
import { useNavigate, useParams } from 'react-router-dom';
import SliderPreview from './SliderPreview';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FaChevronLeft } from 'react-icons/fa';
import rgbHex from 'rgb-hex';
import { URL } from 'url';
import AddProductsImageModal from '../products/new-product/AddProductsImageModal';
import { hexToHSLA } from '../../util/util';
import { Zone } from '../order/OrderInterfaces';

export enum TextPosition {
	left = 'left',
	right = 'right',
}

export interface Slider {
	id?: string;
	name: string;
	description: string;
	color: string;
	backgroundColor: string;
	link: string;
	imageId: string;
	textPosition: TextPosition;
	disabled: boolean;
	ordinal: number | null;
	zone: string[] | null;
}

let initialSlider: Slider = {
	id: '',
	name: '',
	description: '',
	link: '',
	color: '#FFFFFF',
	backgroundColor: '#000000',
	imageId: '',
	textPosition: TextPosition.left,
	disabled: false,
	ordinal: null,
	zone: null,
};

const ManageSlider = () => {
	const [selectedColor, setSelectedColor] = useState({
		h: 250,
		s: 0,
		l: 0.2,
		a: 1,
	});
	const [selectedBackgroundColor, setBackgroundColor] = useState({
		h: 250,
		s: 0,
		l: 0.2,
		a: 1,
	});
	const [sliderFiles, setSliderFiles] = useState<File[]>([]);
	const [sliders, setSliders] = useState<Slider[]>([]);
	const [slider, setSlider] = useState<Slider>(initialSlider);
	const [fileModalOpen, setFileModalOpen] = useState(false);
	const [imageId, setImageId] = useState('');

	const { id } = useParams();
	const [isEdit, setIsEdit] = useState(!!id);

	const [nameValid, setNameValid] = useState(true);
	const [descriptionValid, setDescriptionValid] = useState(true);
	const [linkValid, setLinkValid] = useState(true);
	const [ordinalValid, setOrdinalValid] = useState(true);
	const [imageValid, setImageValid] = useState(true);
	const [zones, setZones] = useState<Zone[] | undefined>([]);
	const [zonesValid, setZonesValid] = useState(true);
	const [selectedZones, setSelectedZones] = useState<string[]>([]);

	const navigate = useNavigate();

	useEffect(() => {
		if (isEdit) {
			fetchSliderInformation(id);
			fetchAllSliders();
		}
	}, [id]);

	useEffect(() => {
		fetchAllSliders();
		fetchZones();
	}, []);

	const fetchAllSliders = async () => {
		const resp = await getSliders();

		if (resp.status == 200) {
			setSliders(resp.data);
		} else {
			alert(`Error fetching sliders: ${resp.status}`);
		}
	};

	const fetchZones = async () => {
		const resp = await getZones();

		if (resp.status == 200) {
			setZones(resp.data);
		} else {
			alert(`Error fetching zones: ${resp.status}`);
		}
	};

	const fetchSliderInformation = async (id: any) => {
		const resp = await getSliderById(id);
		const sliderData = resp.data;

		if (resp.status === 200) {
			const hsla = hexToHSLA(sliderData.color ?? '#000000');
			const hslaBackground = hexToHSLA(sliderData.backgroundColor ?? '#000000');
			setSlider({
				id: sliderData.id,
				name: sliderData.name,
				description: sliderData.description,
				imageId: sliderData.image?.id,
				link: sliderData.link,
				color: sliderData.color,
				backgroundColor: sliderData.backgroundColor,
				textPosition: sliderData.textPosition,
				disabled: sliderData.disabled,
				ordinal: sliderData.ordinal,
				zone: sliderData.zone.map((e: any) => e.id),
			});

			setSelectedZones(sliderData.zone.map((e: any) => e.id));

			setSelectedColor(hsla);
			setBackgroundColor(hslaBackground);

			if (sliderData.image?.id === null) {
				setImageId('');
			} else {
				setImageId(sliderData.image.id);
			}

			setSelectedColor(sliderData.color);
			setBackgroundColor(sliderData.backgroundColor);
		} else alert(`Error fetching slider: ${resp.status}`);
	};

	const handleColorChange = (color: ColorResult, field: 'backgroundColor' | 'color') => {
		const hex = '#' + rgbHex(color.rgb.r, color.rgb.g, color.rgb.b, color.rgb.a);
		if (field === 'color')
			setSelectedColor({
				h: color.hsl.h,
				s: color.hsl.s,
				l: color.hsl.l,
				a: color.hsl.a || 1,
			});
		else if (field === 'backgroundColor')
			setBackgroundColor({
				h: color.hsl.h,
				s: color.hsl.s,
				l: color.hsl.l,
				a: color.hsl.a || 1,
			});
		setSlider({
			...slider,
			[field]: hex,
		});
	};

	function handleRemoveImage() {
		setSliderFiles([]);
		const inputElement = document.getElementById('slider-image-input');
		if (inputElement) {
			(inputElement as HTMLInputElement).value = '';
		}
	}

	const openAddFileModal = () => {
		setFileModalOpen(true);
	};

	const handleSubmit = async () => {
		if (nameValid && descriptionValid && linkValid && imageId !== '' && selectedZones.length > 0) {
			if (isEdit) {
				const updatedSlider = {
					name: slider.name,
					description: slider.description,
					color: slider.color,
					link: slider.link,
					textPosition: slider.textPosition,
					backgroundColor: slider.backgroundColor,
					ordinal: slider.ordinal,
					disabled: slider.disabled,
					imageId: imageId,
					zone: selectedZones,
				};

				await editSlider(slider.id, updatedSlider)
					.then((resp) => {
						if (resp.status === 200) {
							alert('Slider updated successfully.');
							navigate('/sliders');
						} else {
							alert(`Error editing the slider: ${resp}`);
						}
					})
					.catch((error) => {
						alert('Error updating slider: ' + error.message);
					});
			} else {
				const newSlider: Slider = {
					name: slider.name,
					description: slider.description,
					color: slider.color,
					backgroundColor: slider.backgroundColor,
					link: slider.link,
					imageId: imageId,
					textPosition: slider.textPosition,
					ordinal: slider.ordinal,
					disabled: slider.disabled,
					zone: selectedZones,
				};

				await createSlider(newSlider)
					.then((resp) => {
						if (resp.status == 201) {
							alert('Slider created successfully.');
							navigate('/sliders');
						} else {
							alert(`Error creating the slider: ${resp.status}`);
						}
					})
					.catch((error) => {
						alert('Error creating slider: ' + error.message);
					});
			}
		} else {
			alert('Please fill the fields marked with *');
			setNameValid(nameValid);
			setDescriptionValid(descriptionValid);
			setLinkValid(linkValid);
			setImageValid(!!imageId);
			setZonesValid(selectedZones.length > 0);
		}
	};

	const handleFieldChange = (event: any, field: keyof Slider | string) => {
		if (field === 'disabled') {
			setSlider((prevSlider) => ({
				...prevSlider,
				[field]: event.target.checked,
			}));
		} else if (field === 'textPosition') {
			setSlider((prevSlider) => ({
				...prevSlider,
				[field]: event.target.checked ? TextPosition.right : TextPosition.left,
			}));
		} else if (field === 'ordinal') {
			const ordinalValue = parseInt(event.target.value, 10);
			const isValidOrdinal = !isNaN(ordinalValue) && ordinalValue > 0;
			setOrdinalValid(isValidOrdinal);

			if (event.target.value === '') {
				setSlider((prevSlider) => ({
					...prevSlider,
					[field]: null,
				}));
			} else if (isValidOrdinal) {
				setSlider((prevSlider) => ({
					...prevSlider,
					[field]: ordinalValue,
				}));
			}
		} else {
			setSlider((prevSlider) => ({
				...prevSlider,
				[field]: event.target.value,
			}));
		}

		switch (field) {
			case 'name':
				setNameValid(event.target.value !== '');
				break;
			case 'description':
				setDescriptionValid(event.target.value !== '');
				break;
			case 'link':
				setLinkValid(event.target.value !== '');
				break;
			case 'ordinal':
				setOrdinalValid(event.target.value !== '');
				break;
			default:
				break;
		}
	};
	const handleImageChange = async (file: any) => {
		const resp = await uploadFile({ file: file });
		if (resp.status === 201) {
			setFileModalOpen(false);
			console.log(resp.data.id);
			setImageId(resp.data.id);
		}
	};

	const goBack = () => {
		navigate(-1);
	};

	const selectZone = (zones: string[]) => {
		setSelectedZones(zones);
		setZonesValid(zones.length > 0);
	};

	return (
		<div className="py-10 px-4 overflow-auto h-full space-y-6 grid grid-cols-2">
			<div>
				<div className="flex flex-row items-center gap-5 rounded-md shadow-darkGrey shadow-md mb-8">
					<button onClick={goBack} className="flex flex-row gap-2 items-center ml-5">
						<FaChevronLeft /> Back
					</button>
					<div className="text-2xl font-semibold text-black p-5 ">{isEdit ? 'Edit slider' : ' Add new slider'}</div>
				</div>
				<div className="grid grid-rows-7 gap-y-2">
					<div className="w-4/5">
						<TextField label="Name *" className="w-full" value={slider.name} onChange={(e: any) => handleFieldChange(e, 'name')} error={!nameValid} helperText={!nameValid && 'Name is required'} />
					</div>
					<div className="w-4/5">
						<TextField label="Description *" className="w-full" value={slider.description} onChange={(e: any) => handleFieldChange(e, 'description')} error={!descriptionValid} helperText={!descriptionValid && 'Description is required'} />
					</div>
					<div className="w-4/5">
						<TextField label="Link *" className="w-full" value={slider.link} onChange={(e: any) => handleFieldChange(e, 'link')} error={!linkValid} helperText={!linkValid && 'Link is required'} />
						<label htmlFor="" className="text-xs text-gray-500 italic">
							Copy url from laykagift.com. e.g. laykagift.com<b>/products</b>
						</label>
					</div>
					<div className="w-4/5">
						<TextField label="Ordinal *" value={slider.ordinal} InputLabelProps={{ shrink: !!slider.ordinal }} onChange={(e: any) => handleFieldChange(e, 'ordinal')} />
					</div>
					<div className="w-4/5">
						<FormControl fullWidth variant="outlined">
							<InputLabel id="simple-select-label">Zones *</InputLabel>
							<Select labelId="simple-select-label" label={'Zones *'} multiple value={selectedZones} onChange={(e) => selectZone(e.target.value as string[])} name="zones" error={!zonesValid}>
								{zones?.map((zone: any) => (
									<MenuItem key={zone.id} value={zone.id} disabled={zone.disabled} style={{ color: 'black' }}>
										{zone.name}
									</MenuItem>
								))}
							</Select>
							<FormHelperText error={!zonesValid}>{!zonesValid && 'At least one zone is required'}</FormHelperText>
						</FormControl>
					</div>
					<div className="w-4/5 flex flex-row justify-between">
						<div className="">
							<InputLabel>Text color *</InputLabel>
							<ChromePicker className="w-full" disableAlpha={false} color={selectedColor} onChange={(e) => handleColorChange(e, 'color')} />
						</div>
						<div className=" ">
							<InputLabel>Background color *</InputLabel>
							<ChromePicker className="w-full" disableAlpha={false} color={selectedBackgroundColor} onChange={(e) => handleColorChange(e, 'backgroundColor')} />
						</div>
					</div>
					<div className="w-4/5">
						<div className="mt-[5px]">
							<InputLabel htmlFor="slider-image-input" style={{ color: imageValid ? '#aeaeae' : '#ff0000' }}>
								Slider Image *
							</InputLabel>
							<Button onClick={openAddFileModal} variant="contained" color="primary" size="medium" className="w-full py-2 text-xl font-bold min-h-[61px]">
								Add image
							</Button>
							<AddProductsImageModal modalIsOpen={fileModalOpen} closeModal={() => setFileModalOpen(false)} setSelectedFileToList={handleImageChange} isWithinLanguageModal={true} />
							{!imageValid && <FormHelperText style={{ color: '#ff0000' }}>Image is required</FormHelperText>}
						</div>
						<div className="flex space-x-2 mt-2">{imageId && <img src={getImage(imageId)} alt="slider-file" style={{ width: '100px', height: '60%' }} />}</div>
						{sliderFiles.length > 0 && (
							<IconButton onClick={() => handleRemoveImage()}>
								<FaTrash />
							</IconButton>
						)}
					</div>
					<div>
						<FormControlLabel control={<Switch checked={slider.disabled} onChange={(e: any) => handleFieldChange(e, 'disabled')} />} label="Disabled?" />
						<FormControlLabel control={<Switch checked={slider.textPosition === 'right'} onChange={(e: any) => handleFieldChange(e, 'textPosition')} />} label="Text left or right" />
					</div>
					<Button className="w-4/5" color="primary" variant="contained" onClick={handleSubmit}>
						Save changes
					</Button>
				</div>
			</div>
			<div className="col-start-2 col-end-3">
				<div className="flex items-center justify-center h-full">
					{imageId && (
						<SliderPreview
							slider={{
								name: slider.name,
								description: slider.description,
								color: slider.color,
								backgroundColor: slider.backgroundColor,
								image: imageId,
								textPosition: slider.textPosition,
								link: 'slider.link',
							}}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default ManageSlider;
